import { FC, useContext } from 'react';

import { H2, Preloader, Tabs } from '@smartshell/ui-kit';
import cn from 'classnames';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LocalizationContext } from 'localization';
import { ETabs, IBonusDetailedTimeSeriesReport } from 'schemas';

import { CustomTooltip } from '../CustomTooltip';
import s from './DynamicBonus.module.scss';
import { IChartDataItem, IDynamicBonusProps } from './interfaces';
import { getFormattedDate } from './utils';

export const DynamicBonus: FC<IDynamicBonusProps> = ({
  slice,
  setSlice,
  bonusSeriesLoading,
  bonusSeries,
}) => {
  const { t } = useContext(LocalizationContext);

  const transformData = (
    reportData: IBonusDetailedTimeSeriesReport
  ): IChartDataItem[] => {
    if (!reportData?.data || !reportData.keys) return [];

    return reportData.data.map((item, index) => {
      const rawDate = reportData.keys[index];
      const dateOnly = rawDate.split(' ')[0];

      return {
        date: dateOnly,
        accrued: item.values[0] || 0,
        spent: item.values[1] || 0,
      };
    });
  };

  const chartData = bonusSeries
    ? transformData(bonusSeries.bonusDetailedTimeSeriesReport)
    : [];

  const TABS_OPTIONS = [
    { title: t('visitors.slice.day'), value: ETabs.DAY },
    { title: t('visitors.slice.week'), value: ETabs.WEEK },
    { title: t('visitors.slice.month'), value: ETabs.MONTH },
  ];

  return (
    <div className={s.container}>
      <div className={s.header}>
        <H2>{t('report.bonus.dynamic')}</H2>
        <Tabs
          options={TABS_OPTIONS}
          onChange={setSlice}
          value={slice}
          disabled={bonusSeriesLoading}
          className={s.slice}
        />
      </div>

      {bonusSeriesLoading ? (
        <Preloader loading={true} />
      ) : !bonusSeries ? (
        <div className={s.placeholder}>
          {t('transfers.notData.placeholder')}
        </div>
      ) : chartData.every((item) => item.accrued === 0 && item.spent === 0) ? (
        <div className={s.placeholder}>{t('visitors.empty.data')}</div>
      ) : (
        <div className={s.chart}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={500}
              height={300}
              data={chartData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                fontSize={12}
                dataKey="date"
                tickFormatter={getFormattedDate()}
                tick={{ fill: 'var(--text-secondary)' }}
                tickLine={false}
              />
              <YAxis
                fontSize={12}
                type="number"
                tick={{ fill: 'var(--text-secondary)' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={({ payload }) => {
                  return <CustomTooltip data={payload?.[0]?.payload} />;
                }}
              />
              <Legend
                content={() => (
                  <div className={s.legend}>
                    <span className={s.text}>{t('accrued.bonuses')}</span>
                    <span className={cn(s.text, s.orange)}>
                      {t('waste.bonuses')}
                    </span>
                  </div>
                )}
              />
              <Line
                type="monotone"
                dataKey="accrued"
                stroke="#2C87FD"
                dot={false}
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="spent"
                stroke="#FF7E36"
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
